import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, MessageSquare, User, UserPlus, MoreVertical, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { networkAPI } from '../services/network';
import { messagingAPI } from '../services/messaging';
import resumeAPI from '../services/api';

const MyNetworkPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [activeSection, setActiveSection] = useState('connections'); // 'connections' | 'invitations'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = () => {
    setLoading(true);
    setError('');
    Promise.all([
      networkAPI.getMyNetwork().then((list) => setConnections(list || [])),
      networkAPI.getInvitations().then((list) => setInvitations(list || [])),
    ]).catch((e) => {
      console.error('My Network load failed:', e);
      const msg = e?.message || 'Failed to load';
      setError(msg.includes('Network error') ? msg : `Could not load My Network. ${msg}. Try signing out and back in, or check your connection.`);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate]);

  // Refetch when page gains focus so the other user sees updates (e.g. after someone removes connection)
  useEffect(() => {
    const onFocus = () => loadData();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Periodic refetch so the other user sees disconnects without refocusing (e.g. User B's list updates when User A removes them)
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(loadData, 25000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleMessageUser = async (connectedUserId) => {
    try {
      const { conversation } = await messagingAPI.createConversation({ participant_ids: [connectedUserId] });
      const cid = conversation?.conversation_id;
      if (cid) {
        navigate(`/messaging?conversation=${cid}`);
      } else {
        navigate('/messaging');
      }
    } catch (e) {
      console.error('Start conversation failed:', e);
      alert(e?.message || 'Could not start conversation');
    }
  };

  const handleAccept = async (fromUserId) => {
    try {
      await networkAPI.acceptRequest(fromUserId);
      loadData();
      setActiveSection('connections');
    } catch (e) {
      alert(e?.message || 'Failed to accept');
    }
  };

  const handleIgnore = async (fromUserId) => {
    try {
      await networkAPI.ignoreRequest(fromUserId);
      loadData();
    } catch (e) {
      alert(e?.message || 'Failed to ignore');
    }
  };

  const handleDisconnect = async (connectedUserId) => {
    if (!window.confirm('Remove this connection? You can reconnect by sending them a new request.')) {
      return;
    }
    try {
      await networkAPI.disconnect(connectedUserId);
      loadData();
    } catch (e) {
      alert(e?.message || 'Failed to remove connection');
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-custom py-6">
        <div className="flex gap-6">
          {/* Left sidebar: Manage my network */}
          <aside className="w-56 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-md p-4 sticky top-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Manage my network</h2>
              <nav className="space-y-0.5">
                <button
                  type="button"
                  onClick={() => setActiveSection('connections')}
                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between text-sm ${
                    activeSection === 'connections' ? 'bg-primary-50 text-primary-800 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Connections
                  </span>
                  <span className="text-gray-500 font-normal">{connections.length}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection('invitations')}
                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between text-sm ${
                    activeSection === 'invitations' ? 'bg-primary-50 text-primary-800 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Invitations ({invitations.length})
                  </span>
                  {invitations.length > 0 && (
                    <span className="bg-primary-600 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                      {invitations.length}
                    </span>
                  )}
                </button>
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            <div className="bg-white rounded-lg shadow-md p-6 mb-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-7 h-7 text-primary-600" />
                  My Network
                </h1>
                <Link
                  to="/messaging"
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  <MessageSquare className="w-5 h-5" />
                  Open Messaging
                </Link>
              </div>
              <p className="text-gray-600 mt-2">
                {activeSection === 'invitations'
                  ? 'People who want to connect with you. Accept to add them to your connections.'
                  : 'People you\'re connected with. Message them or manage from their profiles.'}
              </p>
            </div>

            {loading && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading...</p>
              </div>
            )}

            {error && (
              <div className="bg-white rounded-lg shadow-md p-6 border border-red-200 bg-red-50">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {!loading && !error && activeSection === 'invitations' && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Invitations ({invitations.length})</h2>
                  {invitations.length > 0 && (
                    <Link to="/profile" className="text-sm text-primary-600 hover:underline">Find people</Link>
                  )}
                </div>
                {invitations.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-md p-12 text-center">
                    <UserPlus className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg text-gray-600">No pending invitations</p>
                    <p className="text-gray-500 mt-2">When someone sends you a connection request, it will appear here.</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md divide-y divide-gray-200">
                    {invitations.map((inv) => (
                      <InvitationRow
                        key={inv.from_user_id}
                        fromUserId={inv.from_user_id}
                        onAccept={() => handleAccept(inv.from_user_id)}
                        onIgnore={() => handleIgnore(inv.from_user_id)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {!loading && !error && activeSection === 'connections' && (
              <>
                {connections.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-md p-12 text-center">
                    <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg text-gray-600">No connections yet</p>
                    <p className="text-gray-500 mt-2">Use Find People on your profile to search and send connection requests.</p>
                    <Link to="/profile" className="btn btn-primary mt-4 inline-block">Go to Profile</Link>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <ul className="divide-y divide-gray-200">
                      {connections.map((conn) => (
                        <NetworkConnectionRow
                          key={`${conn.user_id}-${conn.connected_user_id}`}
                          connectedUserId={conn.connected_user_id}
                          onMessage={handleMessageUser}
                          onDisconnect={handleDisconnect}
                        />
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

function InvitationRow({ fromUserId, onAccept, onIgnore }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!fromUserId) return;
    let cancelled = false;
    resumeAPI.fetchUserProfileByUserId?.(fromUserId).then((p) => {
      if (!cancelled && p) setProfile(p);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fromUserId]);
  if (loading) {
    return (
      <div className="p-4 flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-gray-200 animate-pulse" />
        <div className="flex-1 h-4 bg-gray-200 rounded animate-pulse max-w-[200px]" />
        <div className="w-24 h-8 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }
  const name = profile?.title || profile?.name || 'Unknown';
  const headline = profile?.headline || '';
  const photoUrl = profile?.photo_url || null;
  const profileSlug = profile?.profile_slug || null;
  const publicLinkId = profile?.public_link_id || null;
  const profileIdentifier = profileSlug || publicLinkId;
  return (
    <div className="p-4 flex items-start gap-4">
      <div className="flex-shrink-0">
        {photoUrl ? (
          <img src={photoUrl} alt={name} className="w-14 h-14 rounded-full object-cover bg-gray-100" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center">
            <User className="w-7 h-7 text-primary-600" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {profileIdentifier ? (
          <Link to={`/profile/view?public=${encodeURIComponent(profileIdentifier)}`} className="font-semibold text-gray-900 hover:text-primary-600 truncate block">
            {name}
          </Link>
        ) : (
          <span className="font-semibold text-gray-900 truncate block">{name}</span>
        )}
        <p className="text-sm text-gray-600 mt-0.5">follows you and is inviting you to connect</p>
        {headline && <p className="text-sm text-gray-700 mt-1 truncate">{headline}</p>}
      </div>
      <div className="flex-shrink-0 flex gap-2">
        <button type="button" onClick={onIgnore} className="btn btn-outline text-sm px-3 py-1.5">
          Ignore
        </button>
        <button type="button" onClick={onAccept} className="btn btn-primary text-sm px-3 py-1.5">
          Accept
        </button>
      </div>
    </div>
  );
}

function NetworkConnectionRow({ connectedUserId, onMessage, onDisconnect }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!connectedUserId) return;
    let cancelled = false;
    // Prefer Users table profile (name, photo) via /user/:id/profile; fallback to resume
    (async () => {
      try {
        const fromUser = await resumeAPI.fetchUserProfileByUserId?.(connectedUserId);
        if (cancelled) return;
        if (fromUser) {
          setProfile(fromUser);
          setLoading(false);
          return;
        }
        const fromResume = await resumeAPI.fetchBestResumeForUser?.(connectedUserId, { includeFull: true, includeNonPublic: false });
        if (!cancelled && fromResume) setProfile(fromResume);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [connectedUserId]);

  if (loading) {
    return (
      <li className="p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
        <div className="flex-1 h-4 bg-gray-200 rounded animate-pulse max-w-[200px]" />
        <div className="w-20 h-8 bg-gray-200 rounded animate-pulse" />
      </li>
    );
  }

  const name = profile?.title || profile?.name || 'Unknown';
  const headline = profile?.headline || '';
  const photoUrl = profile?.photo_url || null;
  const profileSlug = profile?.profile_slug || null;
  const publicLinkId = profile?.public_link_id || null;
  const profileIdentifier = profileSlug || publicLinkId;

  return (
    <li className="p-4 flex items-center gap-4 hover:bg-gray-50">
      <div className="flex-shrink-0">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={name}
            className="w-12 h-12 rounded-full object-cover bg-gray-100"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
            <User className="w-6 h-6 text-primary-600" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        {profileIdentifier ? (
          <Link
            to={`/profile/view?public=${encodeURIComponent(profileIdentifier)}`}
            className="font-semibold text-gray-900 hover:text-primary-600 truncate block"
          >
            {name}
          </Link>
        ) : (
          <span className="font-semibold text-gray-900 truncate block">{name}</span>
        )}
        {headline && (
          <p className="text-sm text-gray-600 truncate">{headline}</p>
        )}
      </div>
      <div className="flex-shrink-0 flex items-center gap-1" ref={menuRef}>
        <button
          type="button"
          onClick={() => onMessage(connectedUserId)}
          className="btn btn-outline text-sm px-3 py-1.5 inline-flex items-center gap-1"
        >
          <MessageSquare className="w-3 h-3" />
          Message
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="More options"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full -mt-2 py-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onDisconnect?.(connectedUserId);
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4 flex-shrink-0" />
                Remove connection
              </button>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export default MyNetworkPage;
